import { useWrapAsync } from "@paroicms/front-app-log";
import { useEffect, useState } from "react";
import { fetchReferences } from "../../../../api/application/reference";
import { fetchTableColumnTypes, fetchTables } from "../../../../api/application/table";
import type { APITable } from "../../../../api/infrastructure/table.formatter";
import { getGeneratedReferences } from "../../../../api/shared/get-references";
import { apiRequestWrapper } from "../../../../api/shared/http-client";
import type { SchemaField, SchemaFieldOption } from "../../../../helpers/format-databases";
import { useAppStore } from "../../../../store/app.store";
import useGetDeveloperToken from "../../../hooks/useGetDeveloperToken";
import useGetTableForm from "../../../hooks/useGetTableForm";
import { getTableColumnOptions } from "../../../shared/get-table-column-options";
import { showToast } from "../../../shared/global-tools/toast-system";
import Navbar from "../../parts/Navbar/Navbar";
import NoDataMessage from "../../utils/NoDataMessage/NoDataMessage";
import OurDropdown from "../../utils/OurDropdown/OurDropdown";
import Spinner from "../../utils/Spinner/Spinner";
import ApiCard from "./ApiCard/ApiCard";
import "./DeveloperScreen.scss";

export default function DeveloperScreen() {
  const wrapAsync = useWrapAsync();
  const { generatedReferences, setGeneratedReferences } = useAppStore();
  const [selectedReferenceId, setSelectedReferenceId] = useState<number>();
  const [selectedTableId, setSelectedTableId] = useState<number>();
  const { tableColumnTypes, setTableColumnTypes } = useAppStore();
  const { tableForm, loadingTableForm, fetchTableForm } = useGetTableForm();
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>();
  const [tables, setTables] = useState<APITable[]>();
  const { developerToken, loadingDeveloperToken, regenerateToken } =
    useGetDeveloperToken(selectedReferenceId);

  useEffect(
    wrapAsync(async () => {
      if (tableColumnTypes !== undefined) return;
      await apiRequestWrapper(async () => {
        setTableColumnTypes(await fetchTableColumnTypes());
      });
    }),
    [tableColumnTypes],
  );
  useEffect(
    wrapAsync(async () => {
      if (generatedReferences === undefined) {
        await apiRequestWrapper(async () => {
          const result = await fetchReferences({ limit: 200, page: 1 });
          setGeneratedReferences(getGeneratedReferences(result.data));
        });
      }
    }),
    [generatedReferences],
  );
  useEffect(
    wrapAsync(async () => {
      if (!selectedReferenceId) return;
      await apiRequestWrapper(async () => {
        const result = await fetchTables(selectedReferenceId);
        setTables(result);
      });
    }),
    [selectedReferenceId],
  );
  // useEffect(() => {
  //   if (selectedReferenceId !== undefined && generatedReferences) {
  //     const reference = generatedReferences.find((f) => f.id === selectedReferenceId);
  //     if (!reference) {
  //       throw new Error(`Reference with id '${selectedReferenceId}' not found`);
  //     }
  //     setReference(reference);
  //   }
  // }, [selectedReferenceId, generatedReferences]);
  useEffect(() => {
    const hasSelections = selectedReferenceId && selectedTableId;
    if (!hasSelections) return;
    fetchTableForm({ referenceId: selectedReferenceId, tableId: selectedTableId });
  }, [selectedReferenceId, selectedTableId]);
  useEffect(
    wrapAsync(async () => {
      if (tableForm.length === 0) {
        setSchemaFields([]);
        return;
      }
      const loadedSchemaFields: SchemaField[] = [];
      for (const field of tableForm) {
        let options: SchemaFieldOption[] = [];
        if (!field.type) continue;
        if (field.type === "enum") {
          try {
            options = await getTableColumnOptions(field);
          } catch (error) {
            console.error("Error fetching options for field:", field.name, error);
          }
        }
        loadedSchemaFields.push({
          label: field.label,
          name: field.name,
          type: field.type,
          required: !!field.required,
          options,
          length: field.length,
          default: field.default ? `${field.default}` : undefined,
        });
      }
      setSchemaFields(loadedSchemaFields);
    }),
    [tableForm],
  );

  if (!tableColumnTypes || loadingTableForm || !schemaFields) return <Spinner />;
  if (tableColumnTypes.size === 0) return <NoDataMessage message="Aucun type de champ défini" />;
  if (!generatedReferences) return <Spinner />;
  return (
    <div className="DeveloperScreen">
      <Navbar pageTitle="Développeur" />

      <div className="Container">
        <div className="Container-content">
          <div
            className="Card"
            style={{
              display: "flex",
              alignItems: "end",
              gap: "10px",
              textAlign: "left",
              padding: "15px 21px 39px 26px",
            }}
          >
            <label className="Field w-full">
              <span className="Field-label">Sélectionner un référentiel</span>
              <OurDropdown<number>
                selectedOption={selectedReferenceId}
                initialOptions={generatedReferences.map((d) => ({
                  label: d.name,
                  value: d.id,
                }))}
                onChange={(val) => {
                  setSelectedReferenceId(val);
                  setSelectedTableId(undefined);
                }}
                filter
              />
            </label>
            <label className="Field w-full">
              <span className="Field-label">Sélectionner une table</span>
              <OurDropdown<number>
                selectedOption={selectedTableId}
                initialOptions={tables?.map((d) => ({
                  label: d.name,
                  value: d.id,
                }))}
                onChange={setSelectedTableId}
                filter
              />
            </label>
          </div>

          {/* {selectedReferenceId === undefined ? (
            <NoDataMessage message="Aucun référentiel sélectionné" />
          ) : !reference ? (
            <Spinner />
          ) : (
            <div className="mt-5">
              <h2 style={{ margin: "0", fontSize: "20px" }}>
                Clé API: <small className="DeveloperScreen-token">token</small>{" "}
              </h2>

              <div className="mt-3">
                <ApiCard
                  id={selectedReferenceId}
                  method="GET"
                  label="Récupérer les données du référentiel"
                  schema={schemaFields}
                  tableColumnTypes={tableColumnTypes}
                />
                <ApiCard
                  className="mt-3"
                  id={selectedReferenceId}
                  method="POST"
                  label="Ajouter des données au référentiel"
                  schema={schemaFields}
                  tableColumnTypes={tableColumnTypes}
                />
                <ApiCard
                  className="mt-3"
                  id={selectedReferenceId}
                  method="PUT"
                  label="Mettre à jour une donnée du référentiel"
                  schema={schemaFields}
                  tableColumnTypes={tableColumnTypes}
                />
                <ApiCard
                  className="mt-3"
                  id={selectedReferenceId}
                  method="DELETE"
                  label="Supprimer une donnée du référentiel"
                  schema={schemaFields}
                  tableColumnTypes={tableColumnTypes}
                />
              </div>
            </div>
          )} */}

          {selectedTableId && (
            <div className="mt-5">
              <h2 style={{ margin: "0 0 10px 0", fontSize: "20px" }}>Clé API (X-API-KEY):</h2>
              <div className="DeveloperScreen-token-container">
                <small className="DeveloperScreen-token">{developerToken}</small>
                <button
                  type="button"
                  onClick={() => {
                    if (developerToken) {
                      navigator.clipboard.writeText(developerToken);
                      showToast("Clé API copiée dans le presse-papiers", { severity: "success" });
                    }
                  }}
                  style={{ cursor: "pointer", border: "none" }}
                >
                  <i className="pi pi-copy" aria-label="Copy API token" title="Copier" />
                </button>
                <button
                  type="button"
                  style={{ cursor: "pointer", border: "none" }}
                  onClick={wrapAsync(regenerateToken)}
                  disabled={loadingDeveloperToken}
                >
                  <i
                    className="pi pi-refresh"
                    aria-label="Regenerate API token"
                    title="Régénérer"
                  />
                </button>
              </div>

              <div className="mt-3">
                <ApiCard
                  referenceId={selectedReferenceId}
                  tableId={selectedTableId}
                  method="GET"
                  label="Récupérer les données du référentiel"
                  schema={schemaFields}
                  tableColumnTypes={tableColumnTypes}
                />
                <ApiCard
                  className="mt-3"
                  referenceId={selectedReferenceId}
                  tableId={selectedTableId}
                  method="GET_SHOW"
                  label="Récupérer la donnée du référentiel"
                  schema={schemaFields}
                  tableColumnTypes={tableColumnTypes}
                />
                <ApiCard
                  className="mt-3"
                  referenceId={selectedReferenceId}
                  tableId={selectedTableId}
                  method="POST"
                  label="Ajouter des données au référentiel"
                  schema={schemaFields}
                  tableColumnTypes={tableColumnTypes}
                />
                <ApiCard
                  className="mt-3"
                  referenceId={selectedReferenceId}
                  tableId={selectedTableId}
                  method="PUT"
                  label="Mettre à jour une donnée du référentiel"
                  schema={schemaFields}
                  tableColumnTypes={tableColumnTypes}
                />
                <ApiCard
                  className="mt-3"
                  referenceId={selectedReferenceId}
                  tableId={selectedTableId}
                  method="DELETE"
                  label="Supprimer une donnée du référentiel"
                  schema={schemaFields}
                  tableColumnTypes={tableColumnTypes}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
